/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewInput } from '../../../Components/PIM/common/NewInput';
import { NewSelectMultiSelect } from '../../../Components/PIM/common/NewSelectMultiSelect';
import { NewDatePicker } from '../../../Components/PIM/common/NewDatePicker';
import { NewRadio } from '../../../Components/PIM/common/NewRadio';
import {
  editDocument as editDocumentAction,
  uploadDocument as uploadDocumentAction,
  renewSupplerDocument as renewSupplerDocumentAction,
  fetchSupplierComponents,
  fetchSingleSupplierDetailsAction,
} from '../store/Supplier.action';
import { fetchUserDocumentsList } from '../../UserSetting/store/User.action';
import Loader from '../../../Components/Utility/Loader/Loader';
import UploadFileAndDocument from '../../../Components/UploadFileAndDocument/UploadFileAndDocument';
import { pageSize } from '../../../Components/helper';
import { toStoredCalendarPickerDate } from '../../../Components/helper';

function AddEditSupplierDocument({
  onOpen,
  toggle,
  isEditMode,
  isRenewMode,
  uploadDocument,
  data,
  editDocument,
  renewalData,
  documentsList,
  supplierId,
  userInfo,
  refreshPage,
  renewSupplerDocument,
  isCompanyAdminLoggedIn,
  isCompanyUserLoggedIn,
  updateSupplierDocument,
  isFactoryLevel,
  factoryId,
  setSupportingData,
  setDocs,
  docs,
  handleAddDocument,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();
  const params = useParams();
  const supplierIdFromParams = params?.supplierId;
  const effectiveSupplierId = supplierId || supplierIdFromParams || userInfo?.userId;

  /*
   * Both namespaces are loaded, but i18next resolves a bare key against the
   * first one only, so shared labels below name `common` explicitly rather than
   * being duplicated into the partner-overview bundle.
   */
  const { t } = useTranslation(['modules/partner-overview', 'common']);
  const dispatch = useDispatch();
  const reducer = useSelector((reducerState) => reducerState);
  const { SuppliersDetails } = reducer;
  const { componetSuppliers } = SuppliersDetails;
  const [expiryDueDate, setExpiryDueDate] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const loggedInUserId = userInfo.userId;
  const childRef = useRef(null);
  const [requiredDocumentsList, setRequiredDocumentsList] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState([]);

  const selectedTier = localStorage.getItem('SupplierSelectedTier') || 'Tier1';
  const isProductionLocationTier = selectedTier !== 'Tier1';

  const fetchFactoryList = async () => {
    const resolvedSupplierId = supplierId || supplierIdFromParams || userInfo?.userId;
    const payload = {
      pageIndex: 1,
      pageSize,
      supplierId: resolvedSupplierId,
    };
    const slugName = window.location.hostname?.split('.')?.[0];

    const fetchedDocs = await dispatch(fetchUserDocumentsList());
    await dispatch(fetchSupplierComponents(payload));
    const res = await dispatch(fetchSingleSupplierDetailsAction(resolvedSupplierId));
    const supplierDocumentTypes = res?.data?.companies?.find((company) => company?.companyName === slugName)?.supplierDocumentType || [];
    const availableDocs = fetchedDocs || documentsList || [];
    const documentTypes = availableDocs
      .filter((doc) => supplierDocumentTypes.includes(doc._id))
      .map((doc) => ({
        label: doc.name,
        _id: doc._id,
      }));
    setRequiredDocumentsList(documentTypes);
  };
  useEffect(() => {
    fetchFactoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addDocument = async (submitedData) => {
    const formData = new FormData();

    if (childRef?.current !== null) {
      const uploadedDoc = await childRef.current.selectedDocument();
      let resp = false;
      if (isFactoryLevel && uploadedDoc) {
        const { document } = uploadedDoc;
        const editedData = {
          title: submitedData?.documentName,
          documentType: submitedData?.documentType?._id || '',
          expiredAt: toUtcCalendarDate(expiryDueDate),
          supplierId: effectiveSupplierId,
          loggedInUserId,
          factory: factoryId,
          fileObj: document,
        };
        setSupportingData(editedData);
        if (!isEditMode) {
          formData.append('fileObj', uploadedDoc?.document);
          const status = submitedData?.status === '0' ? 'Approved' : 'Pending';
          formData.append('status', status);
          formData.append('statusColor', status === 'Approved' ? 'green' : 'amber');
        }
        formData.append('supplierId', editedData?.supplierId);
        formData.append('title', editedData?.title);
        formData.append('documentType', editedData?.documentType);
        formData.append('expiredAt', editedData?.expiredAt);
        formData.append('loggedInUserId', editedData?.loggedInUserId);
        if (editedData.factory) {
          formData.append('factory[0]', editedData?.factory);
        }
        if (isEditMode) {
          setShowLoader(true);
          resp = await editDocument(formData, t);
        } else {
          setShowLoader(true);
          resp = await uploadDocument(formData, t);
        }
        if (resp && factoryId) {
          setDocs((prevDocs) => [...prevDocs, resp.data]);
          refreshPage(true);
          toggle();
        }
        if (resp && !factoryId) {
          setDocs((prevDocs) => [...prevDocs, resp.data]);
          handleAddDocument([...docs, resp.data]);
          toggle();
        }
        setShowLoader(false);
      } else {
        let res = false;
        if (uploadedDoc) {
          await setShowLoader(true);
          const { document } = uploadedDoc;
          if (!isEditMode) formData.append('fileObj', document);
          if (!isEditMode && !isRenewMode) {
            const status = submitedData.status === '0' ? 'Approved' : 'Pending';
            formData.append('status', status);
            formData.append('statusColor', status === 'Approved' ? 'green' : 'amber');
          }
          if (isRenewMode) {
            formData.append('status', 'Pending');
            formData.append('statusColor', 'amber');
          }

          const factoryIds = submitedData?.factory ? submitedData?.factory.map((el) => el.factoryId) : submitedData.factoryIds;

          formData.append('supplierId', supplierId || userInfo?.userId);
          formData.append('title', submitedData?.documentName);
          formData.append('documentType', submitedData?.documentType?._id || '');
          formData.append('expiredAt', toUtcCalendarDate(expiryDueDate));
          formData.append('loggedInUserId', loggedInUserId);
          if (factoryIds && factoryIds?.length > 0) {
            factoryIds?.forEach((item, i) => {
              formData.append(`factory[${i}]`, item);
            });
          }

          if (isEditMode) {
            setShowLoader(true);
            res = await editDocument(formData, t);
          } else if (isRenewMode) {
            setShowLoader(true);
            res = await renewSupplerDocument(formData, submitedData._id, t);
          } else {
            setShowLoader(true);
            res = await uploadDocument(formData, t);
          }

          if (res) {
            updateSupplierDocument();
            refreshPage(true);
            toggle();
          }
          setShowLoader(false);
        }
      }
    } else {
      setShowLoader(true);
      const editedData = {
        docId: submitedData._id,
        supplierId: supplierId || userInfo?.userId,
        title: submitedData.documentName,
        documentType: submitedData.documentType?._id || '',
        expiredAt: toUtcCalendarDate(expiryDueDate),
        loggedInUserId,
        factory: submitedData.factory ? submitedData.factory.map((el) => el.factoryId) : submitedData.factoryIds,
      };
      if (isEditMode) {
        const res = await editDocument(editedData, t);
        if (res) {
          updateSupplierDocument();
          refreshPage(true);
          toggle();
        }
      } else {
        const res = await uploadDocument(submitedData, t);
        if (res) {
          updateSupplierDocument();
          refreshPage(true);
          toggle();
        }
      }
      setShowLoader(false);
    }
  };

  const factoryList = useMemo(() => {
    if (Array.isArray(componetSuppliers) && componetSuppliers?.length > 0) {
      return componetSuppliers.map((option) => ({ ...option, label: option?.factoryCode, value: option?.factoryId }));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componetSuppliers]);

  useEffect(() => {
    if (!isEditMode && !isRenewMode) {
      return;
    }
    const dataArray = data || renewalData?.doc;
    if (dataArray && Object.keys(dataArray).length) {
      setShowLoader(true);

      setSelectedFactory([]);
      for (const [key, value] of Object.entries(dataArray)) {
        if (key === 'expiryDueDate') {
          setValue('expiryDueDate', null, { shouldValidate: true });
          setExpiryDueDate(null);
        }
        if (key === 'title') {
          setValue('fileName', value);
          setValue('documentName', value);
        }

        if (key === 'documentType') {
          setValue('documentType', value?._id && value?.name ? { _id: value._id, label: value.name } : null);
          setSelectedDocument(value?._id && value?.name ? { _id: value._id, label: value.name } : null);
        }
        if (key === 'expiredAt') {
          setExpiryDueDate(dataArray?.expiredAt || null);
          setValue('expiryDueDate', dataArray?.expiredAt, {
            shouldValidate: true,
          });
        }
        if (key === 'factory' && dataArray?.factory?.length > 0) {
          const newSelectedFactory = [];
          dataArray?.factory.forEach((factId) => {
            const supplier = factoryList.length > 0 && factoryList?.find((supp) => supp.factoryId.toString() === factId.toString());
            if (supplier) {
              newSelectedFactory.push(supplier);
            }
          });
          setValue('factory', newSelectedFactory, {
            shouldValidate: false,
          });

          setSelectedFactory(newSelectedFactory);
          clearErrors('factory');
        }
        if (key === 'factoryIds') {
          if (dataArray?.factoryIds?.length > 0) {
            const newSelectedFactory = [];
            dataArray?.factoryIds.forEach((factId) => {
              const supplier = factoryList.length > 0 && factoryList?.find((supp) => supp.factoryId.toString() === factId.toString());
              if (supplier) {
                newSelectedFactory.push(supplier);
              }
            });
            setValue('factory', newSelectedFactory, {
              shouldValidate: false,
            });
            setSelectedFactory(newSelectedFactory);
            clearErrors('factory');
          }
        }

        setValue(key, value, { shouldValidate: true });
      }
    } else {
      setValue('expiryDueDate', new Date(), { shouldValidate: true });
    }

    setShowLoader(false);
  }, [data, factoryList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeDocument = (doc) => {
    setValue('documentType', doc, {
      shouldValidate: true,
    });
    setSelectedDocument(doc);
  };

  const handleMultiSelect = (doc) => {
    setValue('factory', doc, {
      shouldValidate: true,
    });
    setSelectedFactory(doc);
  };

  /**
   * Coerces a stored expiry value to what the date picker accepts. The record
   * arrives from the API as an ISO string while the picker takes a `Date`, and
   * feeding it the raw string threw `RangeError: Invalid time value` from its
   * internal `format()`. The legacy picker this replaced parsed strings itself.
   * A stored value is read back as the calendar day it stands for, the same day
   * the list shows, whether it was saved at UTC midnight or as an older local midnight.
   * @param {Date|string|null} value - Stored expiry value.
   * @returns {Date|null} A valid Date, or null when unset or unparseable.
   */
  const toDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    return toStoredCalendarPickerDate(value);
  };

  const handleDate = (date) => {
    setExpiryDueDate(date);
    setValue('expiryDueDate', date, { shouldValidate: true });
  };

  let title;
  if (isEditMode) {
    title = t('SUPPLIER_DOCUMENTS.EDIT_UPLOAD_FILE');
  } else if (isRenewMode) {
    title = t('SUPPLIER_DOCUMENTS.RENEW');
  } else {
    title = t('SUPPLIER_DOCUMENTS.UPLOAD_FILE');
  }

  /**
   * Registers a field with react-hook-form for validation only, withholding the
   * ref: the master-design selects and date picker are function components and
   * cannot take one. Their change handlers write the value back with `setValue`,
   * which is where validation reads it from.
   * @param {string} fieldName - Name of the form field.
   * @param {object} rules - react-hook-form validation rules.
   * @returns {object} Props carrying only the registered field name.
   */
  const registerSelectField = (fieldName, rules) => ({ name: register(fieldName, rules).name });

  const factoryLabel = isProductionLocationTier ? t('ADD_EDIT_SUPLIER.PRODUCTION_LOCATION') : t('ADD_EDIT_SUPLIER.FACTORY');
  const factoryPlaceholder = isProductionLocationTier ? t('ADD_EDIT_SUPPLIER.SELECT_PRODUCTION_LOCATION') : t('ADD_EDIT_SUPPLIER.SELECT_FACTORY');
  const showApproval = !isEditMode && !isRenewMode && !isFactoryLevel && (isCompanyAdminLoggedIn || isCompanyUserLoggedIn);

  return (
    <BombiixModal
      show={onOpen}
      title={title}
      onClose={toggle}
      onSave={handleSubmit(addDocument)}
      saveLabel={t('COMMON_LABEL.SAVE', { ns: 'common' })}
      size="lg"
      className="supplier-document-modal"
      /*
       * The selects portal their menus to <body>, which sits outside the dialog,
       * so react-bootstrap's focus trap would pull focus off an in-menu search
       * the moment it was clicked.
       */
      enforceFocus={false}
    >
      {showLoader ? (
        <Loader />
      ) : (
        <>
          {!isEditMode ? (
            <UploadFileAndDocument
              showTitleField={false}
              acceptedFileExtenstion=".jpeg,.png,xls,.xlsx,.pdf,.ai,.psd,.doc,.txt,.rar,.png,.jpeg,.jpg,.bmp,.tif,.raw,.etc,.gif,.ppt,.svg,.exp,.pptx"
              ref={childRef}
              variant="asset"
            />
          ) : null}

          <div className="partner-form-grid">
            <div className="partner-form-grid__field supplier-document-modal__name">
              <NewInput
                label={t('ADD_EDIT_SUPLIER.DOCUMENT_NAME')}
                required
                placeholder={t('ADD_EDIT_SUPLIER.DOCUMENT_NAME')}
                invalid={Boolean(errors.documentName)}
                {...register('documentName', {
                  required: true,
                  validate: (value) => !!value.trim() || 'no whitespaces.',
                })}
              />
              {errors.documentName && <small className="error-message">{t('ADD_EDIT_SUPLIER.NAME_REQUIRED')}</small>}
            </div>

            <div className="partner-form-grid__field">
              <NewSelectMultiSelect
                label={t('ADD_EDIT_SUPPLIER.SELECT_DOCUMENT_TYPE')}
                required
                isClearable
                showSearch
                searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                options={requiredDocumentsList || []}
                getOptionLabel={({ label }) => `${label}`}
                getOptionValue={({ label }) => `${label}`}
                value={selectedDocument}
                onChange={handleChangeDocument}
                placeholder={t('ADD_EDIT_SUPLIER.SELECT_TYPE')}
                invalid={Boolean(errors?.documentType)}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                {...registerSelectField('documentType', { required: true })}
              />
              {errors?.documentType && <small className="error-message">{t('ADD_EDIT_SUPLIER.TYPE_REQUIRED')}</small>}
            </div>

            {!isFactoryLevel && (
              <div className="partner-form-grid__field">
                <NewSelectMultiSelect
                  isMulti
                  label={factoryLabel}
                  required
                  showSearch
                  searchPlaceholder={t('COMMON_LABEL.SEARCH', { ns: 'common' })}
                  selectAllLabel={t('COMMON_LABEL.SELECT_ALL', { ns: 'common' })}
                  options={factoryList}
                  value={selectedFactory}
                  onChange={(selected) => handleMultiSelect(selected || [])}
                  placeholder={factoryPlaceholder}
                  invalid={Boolean(errors?.factory)}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  {...registerSelectField('factory', { required: true, validate: (value) => !!value })}
                />
                {errors?.factory && (
                  <small className="error-message">
                    {isProductionLocationTier ? t('ADD_EDIT_SUPLIER.PRODUCTION_LOCATION_REQUIRED') : t('ADD_EDIT_SUPLIER.FACTORY_REQUIRED')}
                  </small>
                )}
              </div>
            )}

            <div className="partner-form-grid__field">
              {/*
                `NewDatePicker` takes no `label` prop, so the field label is the
                shared `bombiix-input__label` span, as SampleTrackingDatesModal
                does. The value reaches the payload from component state, which
                `handleDate` keeps in step with the form.
              */}
              <span className="bombiix-input__label">{t('ADD_EDIT_SUPLIER.EXPIRY_DATE')}</span>
              <NewDatePicker
                dateFormat="dd/MM/yyyy"
                placeholder={t('ADD_EDIT_SUPLIER.EXPIRY_DATE')}
                value={toDateValue(expiryDueDate)}
                onChange={(date) => handleDate(date)}
                invalid={Boolean(errors.expiryDueDate)}
              />
              {errors.expiryDueDate && <small className="error-message">{t('ADD_EDIT_SUPLIER.EXPIRY_REQUIRED')}</small>}
            </div>
          </div>

          {showApproval ? (
            <div className="supplier-document-modal__approval">
              <span className="supplier-document-modal__approval-label">{t('ADD_EDIT_SUPLIER.APPROVAL_REQUIRED')}</span>
              <NewRadio id="status1" value={1} defaultChecked label={t('ADD_EDIT_SUPLIER.YES')} {...register('status', { required: false })} />
              <NewRadio id="status0" value={0} label={t('ADD_EDIT_SUPLIER.NO')} {...register('status', { required: false })} />
            </div>
          ) : null}
        </>
      )}
    </BombiixModal>
  );
}

const mapStateToProps = ({ Users, UserDetails }) => {
  const { documentsList } = Users;
  const { userInfo, isCompanyAdminLoggedIn, isCompanyUserLoggedIn } = UserDetails;
  return {
    documentsList,
    userInfo,
    isCompanyAdminLoggedIn,
    isCompanyUserLoggedIn,
  };
};

const mapStateToDispatch = {
  editDocument: editDocumentAction,
  uploadDocument: uploadDocumentAction,
  fetchUserDocumentsList,
  renewSupplerDocument: renewSupplerDocumentAction,
};

export default connect(mapStateToProps, mapStateToDispatch)(AddEditSupplierDocument);

/**
 * Turns a picked expiry day into the stored form: UTC midnight of that calendar
 * day, which is what the document list renders in UTC. Sending the picker's local
 * Date stored the instant before UTC midnight in time zones ahead of UTC, so the
 * list showed a different day than the one picked. A value that is not a Date
 * (an unchanged stored string) is sent as it came.
 * @param {Date|string|null} value - The expiry value held by the form.
 * @returns {string|null} The value to send, or null when unset.
 */
function toUtcCalendarDate(value) {
  if (!value) return null;
  if (!(value instanceof Date)) return value;
  if (Number.isNaN(value.getTime())) return null;
  return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString();
}
